import { batch } from "solid-js";

import { Get, Patch, Post } from "@repo/utils/api";
import Duration from "@repo/utils/Duration";
import FeatureStore from "@repo/utils-solid/FeatureStore";
import Organization from "@repo/vygor-models/Organization";
import { Promotion } from "@repo/vygor-models/Promotion";

import { api } from "..";

export { Promotion };

export type PromotionsRest = {
  [id: Promotion.Id]: Get<{}, { promotion: Promotion }> & {
    "promo-codes": Get<{}, { promoCodes: Promotion.Code[] }>;
  };
};

export namespace PromotionsRest {
  export type Organization = Get<{}, { promotions: Promotion[] }> &
    Post<Promotion.Params, { promotion: Promotion }> & {
      [id: Promotion.Id]: Patch<
        Promotion.Params.Update,
        { promotion: Promotion }
      > & {
        "promo-codes": Get<{}, { promoCodes: Promotion.Code[] }> &
          Post<Promotion.Code.Params, { promoCodes: Promotion.Code[] }> & {
            [id: Promotion.Code.Id]: Patch<
              { expire: boolean },
              { promoCode: Promotion.Code }
            >;
          };
      };
    };
}

export type PromoCodesRest = Get<
  { organizationId: Organization.Id; code: string },
  { promoCode: Promotion.Code }
> & {
  [id: Promotion.Code.Id]: Get<{}, { promoCode: Promotion.Code }>;
};

const initialState = {
  byId: {} as Record<Promotion.Id, Promotion>,
  forOrganization: {
    byId: {} as Record<Organization.Id, Promotion.Id[]>,
  },
  codes: {
    byPromotionId: {} as Record<Promotion.Id, Promotion.Code.Id[]>,
    byId: {} as Record<Promotion.Code.Id, Promotion.Code>,
  },
};

export const { Context, Provider, usePromotions } = FeatureStore.init(
  "Promotions",
  initialState,
  ([store, setStore], { attachAction, wrapActions }) => {
    const accessors = {
      byId: attachAction(
        (id: Promotion.Id | undefined) => id && store.byId[id],
        (id) => id && actions.fetchPromotion(id),
        (id) => (res) => !id || res,
      ),
      forOrganization: {
        byId: attachAction(
          (orgId: Organization.Id | undefined) =>
            orgId &&
            store.forOrganization.byId[orgId]?.map((id) => store.byId[id]!),
          (orgId) => orgId && actions.forOrganization.fetchPromotions(orgId),
          (orgId) => (res) => !orgId || res,
        ),
      },
      codes: {
        for: attachAction(
          (promotionId: Promotion.Id | undefined) =>
            promotionId && store.codes.byPromotionId[promotionId],
          (promotionId) => promotionId && actions.fetchCodesFor(promotionId),
          (promotionId) => (res) => !promotionId || res,
        ),
        byId: attachAction(
          (id: Promotion.Code.Id | undefined) => id && store.codes.byId[id],
          (id) => id && actions.fetchCode(id),
          (id) => (res) => !id || res,
        ),
      },
    };

    const mutations = {
      one(promotion: Promotion) {
        setStore("byId", promotion.id, {
          ...promotion,
          duration: promotion.duration
            ? Duration.String.normalize(promotion.duration)
            : undefined,
        });
      },
      many(promotions: Promotion[]) {
        batch(() => {
          for (const promotion of promotions) {
            mutations.one(promotion);
          }
        });
      },
      codes: {
        one(code: Promotion.Code) {
          setStore("codes", "byId", code.id, code);
        },
        many(codes: Promotion.Code[]) {
          batch(() => {
            for (const code of codes) {
              mutations.codes.one(code);
            }
          });
        },
      },
    };

    const actions = wrapActions({
      one: mutations.one,
      many: mutations.many,
      codes: mutations.codes,
      fetchPromotion(id: Promotion.Id) {
        return api["promotions"][id]!.GET({}).then(({ promotion }) => {
          mutations.one(promotion);
          return promotion;
        });
      },
      fetchCodesFor(promotionId: Promotion.Id) {
        return api["promotions"]
          [promotionId]!["promo-codes"].GET({})
          .then(({ promoCodes }) => {
            batch(() => {
              mutations.codes.many(promoCodes);
              setStore(
                "codes",
                "byPromotionId",
                promotionId,
                promoCodes.map(({ id }) => id),
              );
            });
            return promoCodes;
          });
      },
      fetchCode(id: Promotion.Code.Id) {
        return api["promo-codes"][id]!.GET({}).then(({ promoCode }) => {
          mutations.codes.one(promoCode);
          return promoCode;
        });
      },
      createPromoCodes: (
        orgId: Organization.Id,
        promotionId: Promotion.Id,
        params: Promotion.Code.Params,
      ) => {
        return api.organizations[orgId]!.promotions[promotionId]![
          "promo-codes"
        ].POST(params).then(({ promoCodes }) => {
          batch(() => {
            mutations.codes.many(promoCodes);
            setStore("codes", "byPromotionId", promotionId, (prev = []) => [
              ...prev,
              ...promoCodes.map(({ id }) => id),
            ]);
          });
        });
      },
      expirePromoCode(
        orgId: Organization.Id,
        promotionId: Promotion.Id,
        codeId: Promotion.Code.Id,
      ) {
        return api.organizations[orgId]!.promotions[promotionId]![
          "promo-codes"
        ][codeId]!.PATCH({ expire: true }).then(({ promoCode }) => {
          mutations.codes.one(promoCode);
          return promoCode;
        });
      },

      forOrganization: {
        fetchPromotions(orgId: Organization.Id) {
          return api.organizations[orgId]!["promotions"].GET({}).then(
            ({ promotions }) => {
              batch(() => {
                mutations.many(promotions);
                setStore(
                  "forOrganization",
                  "byId",
                  orgId,
                  promotions.map(({ id }) => id),
                );
              });
            },
          );
        },
        createPromotion: (orgId: Organization.Id, params: Promotion.Params) => {
          return api.organizations[orgId]!["promotions"].POST(params).then(
            ({ promotion }) => {
              batch(() => {
                mutations.one(promotion);
                setStore("forOrganization", "byId", orgId, (promotions) => [
                  ...promotions,
                  promotion.id,
                ]);
              });
              return promotion;
            },
          );
        },
        editPromotion: (
          orgId: Organization.Id,
          promotionId: Promotion.Id,
          params: Promotion.Params.Update,
        ) => {
          return api.organizations[orgId]!["promotions"][promotionId]!.PATCH(
            params,
          ).then(({ promotion }) => {
            mutations.one(promotion);
            return promotion;
          });
        },
        checkPromoCode(organizationId: Organization.Id, code: string) {
          return api["promo-codes"]
            .GET({ organizationId, code })
            .then(({ promoCode }) => {
              mutations.codes.one(promoCode);
              return promoCode;
            });
        },
      },
    });

    return { accessors, actions };
  },
);
